import { BaseViewStore, BaasicDropdownStore } from 'core/stores';
import { applicationContext, QueryUtility } from 'core/utils';
import { action, observable } from 'mobx';
import _ from 'lodash';
import { FacilityOrderTemplateNewFlowInsuranceEditViewStore } from "application/facility-order-new-flow-insurance/stores";
import { FacilityOrderTemplateService, FacilityOrderService, FacilityOrderStatusService } from "application/facility-order/services";
import { ItemCategoryService } from 'application/product/services';
import { LookupService } from "common/services";
import { PickTicketHeaderService } from "application/pick-ticket/services";
import { ScheduleService} from 'application/schedule/services';
import { LocalPatientService } from "application/patient/services";

@applicationContext
class FacilityOrderNewFlowInsurancePreviewViewStore extends BaseViewStore {
    @observable accessor facilityOrderTemplateStores;
    @observable accessor selectedStatusAbrv;
    @observable accessor fetchingTemplates = false;
    @observable accessor disabled = false;
    @observable accessor disabledSendOrder = false;
    @observable accessor isStatusBarVisible = true;
    @observable accessor disabledStatus = false;
    @observable accessor pickTicketHeaderStatus;
    @observable accessor facilityOrder = null;
    @observable accessor notification;
    
    tabItems = [{ abrv: 'in-progress', name: 'In Progress' }, { abrv: 'new-patient', name: 'New Patients' }, { abrv: 'change', name: 'Changes' }, { abrv: 'no-change', name: 'No Changes' }, { abrv: 'discharge', name: 'Discharges' }];

    constructor(rootStore) {
        super(rootStore);

        this.id = rootStore.routerStore.routerState.params.id;
        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);

        this.itemCategoryStore = new BaasicDropdownStore({
            multi: true, filterable: true, initFetch: false
        }, {
            fetchFunc: async (term) => {
                const response = await this.itemCategoryService.find({
                    searchQuery: term,
                    rpp: 100
                });
                let filteredCategories = response.data.item.filter(i => this.facilityOrder.isEnteralWeek ? i.abrv === "enteral" : (i.abrv === "enteral" || i.abrv === "respiratory" || i.abrv === "urological"
                    || i.abrv === "ostomy" || i.abrv === "wound-care"));
                return filteredCategories;
            },
        });

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            await this.fetchFacilityOrderTemplates(filter);
        }, {});

        this.fetch([
            this.initialize()
        ]);
    }

    @action.bound
    async initialize() {
        await this.initializeFacilityOrder();
        await this.initializeLookups();
        await this.setStatus(this.selectedStatusAbrv);

        const typeResponse = await this.scheduleService.getTypes();
            this.orderType = _.find(typeResponse.item, {abrv: 'ordering'});
    }

    @action.bound
    async initializeFacilityOrder() {
        const response = await this.facilityOrderService.get(this.id, { embed: 'Facility,Facility.FacilityAreaOfCares,FacilityOrderPatients,FacilityOrderStatuses,ImportHeaderStatus' });
        this.facilityOrder = response.data;
        this.isInventoryStepAllowed = (await this.facilityOrderService.isInventoryStepAllowed(this.id)).data;
        this.pickTicketHeaderId = this.facilityOrder.pickTicketHeaderId;
        const localPatientService = new LocalPatientService(this.rootStore.application.baasic.apiClient);
        const masterPatient = (await localPatientService.getByPatientKey(this.facilityOrder.facility.masterPatientId, { fields: 'id' })).data; 
        this.masterPatientId = masterPatient ? masterPatient.id : undefined;
        await this.initializePickTicketHeader();
        await this.itemCategoryStore.filterAsync();
        this.rootStore.titleStore.setTitle(`Insurance Review - ${this.facilityOrder.facility.name}`);
        this.facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'insurance-review');
        this.isInsuranceStatus = this.facilityOrder.importHeaderStatus && this.facilityOrder.importHeaderStatus.abrv === "insurance-review";
        this.initialIntakeStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'initial-intake');
        this.usIntakeStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "us-intake");
        this.disabled = !(this.facilityOrderStatus && this.facilityOrderStatus.startedOn && !this.facilityOrderStatus.completedOn) || this.isDisabled();
        this.disabledSendOrder = !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder;
    }

    @action.bound
    async initializePickTicketHeader() {
        if(!this.pickTicketHeaderId){
            this.pickTicketHeaderId = (await this.facilityOrderService.get(this.id, {fields: 'PickTicketHeaderId'})).data.pickTicketHeaderId;
        }
        if (this.pickTicketHeaderId) {
            const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
            const pickTicketHeader = (await pickTicketHeaderService.get(this.pickTicketHeaderId, { embed: "pickTicketHeaderStatus" })).data;
            this.pickTicketHeaderStatus = pickTicketHeader && pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv;
        }
    }

    @action.bound
    isDisabled() {
        return (this.usIntakeStatus || !(this.initialIntakeStatus && this.initialIntakeStatus.completedOn)) && !(this.facilityOrderStatus && this.facilityOrderStatus.completedOn)
    }

    @action.bound
    async initializeLookups() {
        this.lookups = await this.lookupService.getLookups("NewFlowStatus,ItemCategory,ImportHeaderStatus");
        this.enteralItemCategoryId = _.find(this.lookups.itemCategory, s => { return s.abrv === 'enteral'; }).id;

        let queryParams = this.rootStore.routerStore.routerState.queryParams;
        this.selectedStatusAbrv = _.find(this.lookups.newFlowStatus, s => {
            return queryParams && queryParams.newFlowInsuranceStatusId ? s.id === queryParams.newFlowInsuranceStatusId : s.abrv === 'in-progress';
        }).abrv;
    }

    @action.bound
    async setStatus(statusAbrv) {
        this.selectedStatusAbrv = statusAbrv;
        const status = _.find(this.lookups.newFlowStatus, s => {
            return s.abrv === statusAbrv;
        });

        const statusId = status && status.id;
        this.queryUtility.filter.set('newFlowInsuranceStatusId', statusId);
        this.queryUtility.fetch();
    }

    @action.bound
    async fetchFacilityOrderTemplatesByPatient(patientId) {
        var itemCategories = _.map(this.itemCategories, (i => { return i.id }));
        let params = {
            ...this.queryUtility.plainFilter,
            pageSize: 10000,
            facilityOrderId: this.id,
            patientId: patientId,
            embed: 'FacilityOrderTemplateInitial,FacilityOrderTemplateItems,Patient,SubCategory,ItemCategory,ImportReference,NewFlowStatus,NewFlowInsuranceStatus,NewFlowUSIntakeStatus,ReviewUSIntakeValidations,FacilityOrderTemplateMissingDocuments,OrderingDoctor,ClinicalStatus',
            orderBy: "SubCategory",
            orderDirection: "asc",
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : (itemCategories && itemCategories.length > 0) ? itemCategories : undefined,
            masterPatientId: this.facilityOrder.isEnteralWeek ? this.masterPatientId : undefined
        }
        return (await this.facilityOrderTemplateService.find(params)).data.item;
    }

    @action.bound
    async fetchFacilityOrderTemplates(filter) {
        this.fetchingTemplates = true;
        var itemCategories = this.itemCategoryStore && _.map(this.itemCategoryStore.value, (i => { return i.id }));
        let params = {
            ...filter,
            pageSize: 10000,
            facilityOrderId: this.id,
            embed: 'FacilityOrderTemplateInitial,FacilityOrderTemplateItems,Patient,SubCategory,ItemCategory,ImportReference,NewFlowStatus,NewFlowInsuranceStatus,ClinicalStatus',
            orderBy: "SubCategory",
            orderDirection: "asc",
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : (itemCategories && itemCategories.length > 0) ? itemCategories : undefined,
            masterPatientId: this.facilityOrder.isEnteralWeek ? this.masterPatientId : undefined
        }
        let facilityOrderTemplates = (await this.facilityOrderTemplateService.find(params)).data.item;
        if(this.selectedStatusAbrv === 'new-patient'){
            facilityOrderTemplates = facilityOrderTemplates.filter(i => i.newFlowInsuranceStatus.abrv !== "discharge" && i.newFlowInsuranceStatus.abrv === "change");
        }
        let grouped = _.groupBy(facilityOrderTemplates, 'patientId');
        let facilityOrderTemplatesGroupByPatient = [];
        
        _.each(grouped, (val, key) => {
            if (this.selectedStatusAbrv === 'new-patient' ? this.isNewPatient(key) : ((this.selectedStatusAbrv === 'change' || this.selectedStatusAbrv === 'no-change') && this.isNewPatient(key)) ? false  : true) {
                facilityOrderTemplatesGroupByPatient.push({ id: key, item: val, facilityOrder: this.facilityOrder, lookups: this.lookups, selectedStatusAbrv: this.selectedStatusAbrv, selectedItemCategories: itemCategories, checkIsInsuranceReviewCompleted: () => this.checkIsInsuranceReviewCompleted(), fetchTemplatesByPatientId: () => this.fetchFacilityOrderTemplatesByPatient(key) });
            }
        });

        this.facilityOrderTemplateStores = facilityOrderTemplatesGroupByPatient.map(item =>
            new FacilityOrderTemplateNewFlowInsuranceEditViewStore(this.rootStore, item)
        );
        this.fetchingTemplates = false;
    }

    @action.bound
    isNewPatient(patientId) {
        const facilityOrderPatient = this.facilityOrder && this.facilityOrder.facilityOrderPatients && this.facilityOrder.facilityOrderPatients.find(i => i.patientId === patientId);
        if (facilityOrderPatient) {
            return facilityOrderPatient.isNewPatient;
        }
        return false;
    }

    @action.bound
    async checkIsInsuranceReviewCompleted() {
        if (!this.facilityOrderTemplateStores.some(i => i.items && i.items.length > 0) && (this.facilityOrderStatus && !this.facilityOrderStatus.completedOn)) {
            await this.initializeFacilityOrder();
            await this.setStatus(this.selectedStatusAbrv);
        }
    }

    @action.bound
    async downloadFacilityOrder() {
        const downloadUrl = `${(await this.facilityOrderTemplateService.getDownloadNewFlowUrl({ ...this.queryUtility.plainFilter, facilityOrderId: this.facilityOrder.id }))}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl
            }&ttype=${this.rootStore.authStore.token.type}`
        window.open(downloadUrl, "_blank")
    }

    @action.bound
    async setInsuranceStarted() {
        if (this.facilityOrderStatus)
            return;

        this.disabledStatus = true;
        try {
            const status = this.lookups.importHeaderStatus.find(i => i.abrv === 'insurance-review');
            var response = await this.facilityOrderStatusService.create({ facilityOrderId: this.facilityOrder.id, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id });
            await this.getFacilityOrderStatus();
            
            if(response.data){
                this.facilityOrderStatus = response.data;
                this.disabled = false;
                const temp = this.facilityOrderTemplateStores;
                this.facilityOrderTemplateStores = null;
                this.facilityOrderTemplateStores = temp;
            }
        }
        catch(err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
            await this.getFacilityOrderStatus();
        }
        this.disabledStatus = false;
    }

    @action.bound
    async setInsuranceCompleted() {
        if (!this.facilityOrderStatus || this.facilityOrderStatus.completedOn)
            return;
        
        this.disabledStatus = true;
        try {
            const request = { ...this.facilityOrderStatus, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id };
            await this.facilityOrderStatusService.update(request);
            this.facilityOrderStatus = request;
            this.disabled = true;
        }
        catch(err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        await this.getFacilityOrderStatus();
        this.disabledStatus = false;
    }

    @action.bound
    async restartInsuranceReview() {
        this.disabledStatus = true;
        try {
            const request = { ...this.facilityOrderStatus, completedOn: null, completedByUserId: null };
            await this.facilityOrderStatusService.update(request);
            this.facilityOrderStatus = request;
            this.disabled = false;
        }
        catch(err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        await this.getFacilityOrderStatus();
        this.disabledStatus = false;
    }

    @action.bound
    changeStatusBarVisibility() {
        this.isStatusBarVisible = !this.isStatusBarVisible;
    }

    @action.bound
    async getFacilityOrderStatus() {
        var statusesResponse = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        if(statusesResponse.data){
            this.facilityOrder.facilityOrderStatuses = statusesResponse.data.item;
            this.initialIntakeStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'initial-intake');
        }

        const response = await this.facilityOrderService.get(this.id, { embed: 'ImportHeaderStatus', fields:"ImportHeaderStatus" });
        if(response.data){
            this.facilityOrder.importHeaderStatus = response.data.importHeaderStatus;
            this.isInsuranceStatus = this.facilityOrder.importHeaderStatus && this.facilityOrder.importHeaderStatus.abrv === "insurance-review";
        }

        this.changeStatusBarVisibility();
        this.changeStatusBarVisibility();
    }

    @action.bound
    signalRAction(message) {
        if (message.text && (message.userId != this.rootStore.userStore.user.id && this.facilityOrderTemplateStores &&  this.facilityOrderTemplateStores.some(fo=> fo && ((fo.items && fo.items.some(i => i && i.id == message.id)) || fo.id == message.id)))) {
            this.notification = message.text;
        } 
        else if (message.table == "PTH") {
            this.initializePickTicketHeader();
        }
        else if (message.column == "SendOrder") {
            this.facilityOrder.sendOrder = true;
            this.disabledSendOrder = !this.isAdmin;
        }
    }
    
    @action.bound
    async goToFacilityPreview() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
    }

    @action.bound
    async goToFacilityCalendar() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderType.id}`);
    }
}

export default FacilityOrderNewFlowInsurancePreviewViewStore;
